import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { FaEnvelope, FaArrowLeft, FaCheckCircle, FaPaperPlane } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import images from '../assets/images';
import logo from '../assets/logo.png';
import SEO, { PageSEO } from '../components/SEO';
import BrandName from '../components/BrandName';

const ForgotPassword = () => {
  const { forgotPassword } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [sentEmail, setSentEmail] = useState('');

  const formik = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email('Invalid email address')
        .required('Email is required'),
    }),
    onSubmit: async (values) => {
      setIsLoading(true);
      try {
        await forgotPassword(values.email);
        setSentEmail(values.email);
        setEmailSent(true);
        toast.success('Password reset email sent!');
      } catch (error) {
        const raw = error?.message || '';
        const message = raw.includes('rate limit') || raw.includes('over_email_send_rate_limit')
          ? 'Too many requests. Please wait a few minutes before trying again.'
          : raw || 'Failed to send reset email. Please try again.';
        toast.error(message);
      } finally {
        setIsLoading(false);
      }
    },
  });

  const handleResend = async () => {
    setIsLoading(true);
    try {
      await forgotPassword(sentEmail);
      toast.success('Reset email resent successfully!');
    } catch (error) {
      toast.error('Failed to resend email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <SEO {...PageSEO.forgotPassword} />
      <div className="min-h-screen flex">
      {/* Left Side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-900/90 via-primary-800/80 to-success-900/90 z-10"></div>
        <img
          src={images.auth.login}
          alt="Fitness Journey"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 z-20 flex flex-col justify-center items-center text-white p-12">
          <div className="max-w-md text-center">
            <img src={logo} alt="Fitnession" className="h-16 w-auto mx-auto mb-6 brightness-0 invert" />
            <h2 className="text-4xl font-bold mb-4">Reset Your Password</h2>
            <p className="text-xl text-white/80 mb-8">
              Don't worry, it happens to the best of us. We'll help you get back on track.
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-md w-full">
          {/* Logo and Header */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center space-x-2 mb-6">
              <img src={logo} alt="Fitnession" className="h-10 w-auto" />
              <BrandName className="text-3xl" />
            </Link>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Forgot Password?</h2>
            <p className="text-gray-600">
              {emailSent
                ? 'Check your email for reset instructions'
                : 'Enter your email and we\'ll send you reset instructions'
              }
            </p>
          </div>

          {/* Form or Success Message */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            {emailSent ? (
              /* Success State */
              <div className="text-center">
                <div className="w-20 h-20 bg-success-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <FaPaperPlane className="text-4xl text-success-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Email Sent!</h3>
                <p className="text-gray-600 mb-6">
                  We've sent a password reset link to:
                  <br />
                  <span className="font-medium text-gray-900">{sentEmail}</span>
                </p>
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <p className="text-sm text-gray-600">
                    The link will expire in <span className="font-medium">1 hour</span>.
                    <br />
                    Check your spam folder if you don't see the email.
                  </p>
                </div>
                <button
                  onClick={handleResend}
                  disabled={isLoading}
                  className="w-full py-3 px-4 border border-primary-600 rounded-lg text-primary-600 font-medium hover:bg-primary-50 transition-colors disabled:opacity-50"
                >
                  {isLoading ? 'Sending...' : 'Resend Email'}
                </button>
                <Link
                  to="/login"
                  className="mt-4 inline-flex items-center justify-center w-full py-3 px-4 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors"
                >
                  <FaArrowLeft className="mr-2" />
                  Back to Login
                </Link>
              </div>
            ) : (
              /* Form State */
              <form onSubmit={formik.handleSubmit} className="space-y-6">
                {/* Email Field */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaEnvelope className="text-gray-400" />
                    </div>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      className={`block w-full pl-10 pr-3 py-3 border ${
                        formik.touched.email && formik.errors.email
                          ? 'border-red-300'
                          : 'border-gray-300'
                      } rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent`}
                      placeholder="you@example.com"
                      {...formik.getFieldProps('email')}
                    />
                  </div>
                  {formik.touched.email && formik.errors.email && (
                    <p className="mt-1 text-sm text-red-600">{formik.errors.email}</p>
                  )}
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Sending...
                    </div>
                  ) : (
                    'Send Reset Link'
                  )}
                </button>

                {/* Back to Login Link */}
                <div className="text-center">
                  <Link
                    to="/login"
                    className="inline-flex items-center text-sm font-medium text-primary-600 hover:text-primary-500"
                  >
                    <FaArrowLeft className="mr-2" />
                    Back to Login
                  </Link>
                </div>
              </form>
            )}
          </div>

          {/* Trust Badges */}
          <div className="mt-8 flex items-center justify-center space-x-6 text-sm text-gray-500">
            <div className="flex items-center">
              <FaCheckCircle className="text-success-500 mr-2" />
              <span>Secure Reset</span>
            </div>
            <div className="flex items-center">
              <FaCheckCircle className="text-success-500 mr-2" />
              <span>256-bit SSL</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default ForgotPassword;
